import React, { useState, useEffect } from 'react'
import { PROJ_COLORS } from '../lib/data'
import { fmtPhone } from '../lib/dateUtils'
import { saveActiveOverride, setJobActive, setEmployeeActive, setEmployeeSchedulable, triggerFoundationSync, loadSyncLog, saveEmployeeNotificationEmail, nameFootprint, setEmployeeDisplayName, renameInFutureSchedule } from '../lib/db'

// Effective (app-wide) name key: what every name-keyed reader compares on.
const nameKeyOf = (n) => String(n == null ? '' : n).trim().toLowerCase()

// ── Display-name footprint guard ───────────────────────────────────────────
// employees.name is a JOIN KEY (schedule cells, field reports, office logins, dispatch
// texts, Sarah's matcher, the payroll CSV name fallback — see nameFootprint in
// src/lib/db.js). A rename orphans whatever is keyed on the old name, so the rule is:
// a display name goes on the duplicate with NO footprint, never on the incumbent.
//
// ⚠ THE ID-ANCHORED COUNT IS WHAT DECIDES. In a duplicate-name pair both twins match the
// same NAME rows by definition — a name-only count cannot tell them apart and would
// block BOTH, including the new employee the rule says is the safe one. Rows anchored
// to this employee's ID (worker_ids / foreman_id, attendance employee_id, dispatch
// recipient employee_id, app_users.employee_id) are what block; when the name count is
// non-zero but the id count is zero, those rows belong to the OTHER holder of the name
// and renaming this one is safe — the form says so instead of blocking. UNKNOWN (the
// report count could not be fetched) stays blocked.
const footprintIdTotal = (fp) => !fp ? 0 :
  fp.idCells + fp.idLogin + (fp.idReportsInCrew || 0) + (fp.idFutureDispatch || 0)
const footprintNameTotal = (fp) => !fp ? 0 :
  fp.cells + fp.login + (fp.reportsAsForeman || 0) + (fp.reportsInCrew || 0) + (fp.futureDispatch || 0)
const plural = (n, one, many) => `${n} ${n === 1 ? one : (many || one + 's')}`
// byId=true → the rows anchored to this employee's id; false → the rows matching the name.
const footprintParts = (fp, byId) => {
  if (!fp) return []
  const parts = []
  if (byId) {
    if (fp.idCells) parts.push(plural(fp.idCells, 'schedule cell') + (fp.idForemanCells ? ` (${fp.idForemanCells} as foreman)` : ''))
    if (fp.reportsUnknown) parts.push('field reports: COULD NOT BE COUNTED')
    else {
      if (fp.idReportsInCrew) parts.push(plural(fp.idReportsInCrew, 'field report') + ' in the crew')
      if (fp.idFutureDispatch) parts.push(plural(fp.idFutureDispatch, 'upcoming dispatch text'))
    }
    if (fp.idLogin) parts.push(plural(fp.idLogin, 'office login'))
    return parts
  }
  if (fp.cells) parts.push(plural(fp.cells, 'schedule cell') + (fp.foremanCells ? ` (${fp.foremanCells} as foreman)` : ''))
  if (fp.costCodeKeys) parts.push(plural(fp.costCodeKeys, 'per-worker cost code'))
  if (fp.reportsUnknown) parts.push('field reports: COULD NOT BE COUNTED')
  else {
    if (fp.reportsAsForeman) parts.push(plural(fp.reportsAsForeman, 'field report') + ' as foreman')
    if (fp.reportsInCrew) parts.push(plural(fp.reportsInCrew, 'field report') + ' in the crew')
    if (fp.futureDispatch) parts.push(plural(fp.futureDispatch, 'upcoming dispatch text'))
  }
  if (fp.login) parts.push(plural(fp.login, 'office login'))
  return parts
}
const CONFIRM_WORD = 'RENAME'

const TRADES = [
  'Masons Foreman', 'Masons', 'Laborers', 'Carpenters Foreman',
  'Carpenters', 'Ironworkers', 'Operator Engineer', 'Electricians',
  'Plumbers', 'HVAC', 'Roofers', 'Painters', 'Other'
]
const LOCALS = [
  'Local 3', 'Local 4', 'Local 5', 'Local 11', 'Local 29',
  'Local 253', 'Local 254', 'Local 825', 'Other'
]

// ── vCard export ───────────────────────────────────────────────────────────
function toE164(phone) {
  if (!phone) return null
  const digits = String(phone).replace(/\D/g, '')
  if (digits.length === 10) return `+1${digits}`
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`
  return digits ? `+${digits}` : null
}

function vEscape(s) {
  return String(s).replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\n/g, '\\n')
}

function splitName(fullName) {
  const parts = String(fullName || '').trim().split(/\s+/)
  if (parts.length <= 1) return { first: parts[0] || '', last: '' }
  return { first: parts.slice(0, -1).join(' '), last: parts[parts.length - 1] }
}

function buildVCard(emp) {
  const cell = toE164(emp.cellPhone)
  const work = toE164(emp.phone)
  if (!cell && !work) return null
  const { first, last } = splitName(emp.name)
  const title = [emp.trade, emp.local].filter(Boolean).join(' · ')
  const lines = [
    'BEGIN:VCARD',
    'VERSION:3.0',
    `FN:${vEscape(emp.name)}`,
    `N:${vEscape(last)};${vEscape(first)};;;`,
  ]
  if (cell) lines.push(`TEL;TYPE=CELL:${cell}`)
  if (work && work !== cell) lines.push(`TEL;TYPE=WORK:${work}`)
  lines.push('ORG:Cornerstone Contracting')
  if (title) lines.push(`TITLE:${vEscape(title)}`)
  lines.push('END:VCARD')
  return lines.join('\r\n')
}

function exportContactsVcf(roster) {
  const cards = roster
    .filter(e => e.active !== false)
    .map(buildVCard)
    .filter(Boolean)
  if (cards.length === 0) return
  const blob = new Blob([cards.join('\r\n') + '\r\n'], { type: 'text/vcard;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = 'cornerstone-crew-contacts.vcf'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

// ── Shared small components ────────────────────────────────────────────────
function SectionHeader({ title, sub, action }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
      <div>
        <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>{title}</div>
        {sub && <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>{sub}</div>}
      </div>
      {action}
    </div>
  )
}

function FormRow({ label, children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
      <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--text2)', minWidth: 80 }}>{label}</label>
      <div style={{ flex: 1 }}>{children}</div>
    </div>
  )
}

function Checkbox({ checked, onChange, label }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer', fontSize: 13 }}>
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)}
        style={{ width: 15, height: 15, cursor: 'pointer' }} />
      {label}
    </label>
  )
}

// ── Employee form ──────────────────────────────────────────────────────────
function EmployeeForm({ emp, roster = [], onSave, onCancel }) {
  const [name, setName] = useState(emp?.name || '')
  const [trade, setTrade] = useState(emp?.trade || 'Masons')
  const [local, setLocal] = useState(emp?.local || 'Local 4')
  const [isForeman, setIsForeman] = useState(emp?.isForeman || false)
  const [email, setEmail] = useState(emp?.email || '')
  const [customTrade, setCustomTrade] = useState('')
  const [customLocal, setCustomLocal] = useState('')
  const [error, setError] = useState('')
  const [displayName, setDisplayName] = useState(emp?.displayName || '')
  // null = still counting; an object once nameFootprint resolves. Same for each
  // same-name sibling, so the block can point at the SAFER one to rename instead.
  const [footprint, setFootprint] = useState(null)
  const [siblingFps, setSiblingFps] = useState({})
  const [confirmText, setConfirmText] = useState('')

  const siblings = emp ? roster.filter(e => e.id !== emp.id && nameKeyOf(e.name) === nameKeyOf(emp.name)) : []
  useEffect(() => {
    if (!emp) return
    let on = true
    nameFootprint(emp.id, emp.name).then(fp => { if (on) setFootprint(fp) })
    Promise.all(siblings.slice(0, 3).map(sib => nameFootprint(sib.id, sib.name).then(fp => [sib.id, fp])))
      .then(pairs => { if (on) setSiblingFps(Object.fromEntries(pairs)) })
    return () => { on = false }
  }, [emp?.id])

  const displayChanged = !!emp && (displayName.trim() !== (emp.displayName || '').trim())
  const fpIdTotal = footprintIdTotal(footprint)
  const fpNameTotal = footprintNameTotal(footprint)
  // Blocked on the ID-anchored count (or unknown) — never on the name count alone.
  const fpBlocked = !!emp && (footprint === null || footprint.reportsUnknown || fpIdTotal > 0)
  // Name rows exist but none are anchored to THIS id: they are the other twin's.
  const fpInfoOnly = !!emp && !fpBlocked && footprint !== null && fpNameTotal > 0
  // The same-name sibling with the smallest ID footprint — the one that SHOULD carry the
  // display name. Only named once every sibling has been counted.
  const siblingsCounted = siblings.length > 0 && siblings.every(sib => siblingFps[sib.id])
  const saferSibling = (() => {
    if (!fpBlocked || !siblingsCounted) return null
    const best = siblings.slice().sort((a, b) => footprintIdTotal(siblingFps[a.id]) - footprintIdTotal(siblingFps[b.id]))[0]
    return best && footprintIdTotal(siblingFps[best.id]) < fpIdTotal ? best : null
  })()
  // For the informational note: which twin actually owns the name rows (by id).
  const rowOwner = fpInfoOnly && siblingsCounted
    ? siblings.find(sib => footprintIdTotal(siblingFps[sib.id]) > 0) || null
    : null

  const handleSave = () => {
    if (!name.trim()) { setError('Name is required'); return }
    if (displayChanged) {
      const v = displayName.trim()
      const effective = v || emp.foundationName || emp.name
      const clash = roster.find(e => e.id !== emp.id && nameKeyOf(e.name) === nameKeyOf(effective))
      if (clash) { setError(`Another employee is already named ${clash.name} (#${clash.id}).`); return }
      if (fpBlocked && confirmText.trim() !== CONFIRM_WORD) {
        setError(footprint === null
          ? 'Still counting what is keyed on this name — wait a moment, then try again.'
          : `This name is in use elsewhere. Type ${CONFIRM_WORD} to rename anyway, or set the display name on the other employee instead.`)
        return
      }
    }
    const finalTrade = trade === 'Other' ? customTrade : trade
    const finalLocal = local === 'Other' ? customLocal : local
    if (!finalTrade) { setError('Trade is required'); return }
    if (!finalLocal) { setError('Local is required'); return }
    onSave({ name: name.trim(), trade: finalTrade, local: finalLocal, isForeman, email: email.trim(), displayName: displayName.trim() })
  }

  return (
    <div className="modal-backdrop" onClick={onCancel}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-title">{emp ? 'Edit employee' : 'Add employee'}</div>
        <div className="modal-sub">{emp ? `Editing ${emp.name}` : 'Add a new worker to your roster'}</div>
        {error && <div style={{ background: 'var(--red-bg)', color: 'var(--red-text)', borderRadius: 8, padding: '8px 12px', fontSize: 12, marginBottom: 10 }}>{error}</div>}
        <FormRow label="Full name">
          {emp ? (
            <span style={{ fontSize: 13, color: 'var(--text2)', padding: '7px 0', flex: 1 }}>
              {name} <span style={{ fontSize: 11, color: 'var(--text3)' }}>(synced from Foundation)</span>
            </span>
          ) : (
            <input className="form-input" value={name} onChange={e => setName(e.target.value)} placeholder="First Last" autoFocus />
          )}
        </FormRow>
        {emp && (
          <FormRow label="Display name">
            <div>
              {fpInfoOnly && (
                <div style={{ background: 'var(--blue-bg)', color: 'var(--blue-text)', borderRadius: 8, padding: '8px 12px', fontSize: 12, marginBottom: 6, lineHeight: 1.45 }}>
                  ℹ {footprintParts(footprint, false).join(' · ')} match the name "{emp.name}", but none are anchored to this record (#{emp.id}).
                  {rowOwner
                    ? <> They belong to <strong>#{rowOwner.id} {rowOwner.name}</strong>.</>
                    : siblings.length ? <> They belong to the other employee with this name ({siblings.map(x => `#${x.id}`).join(', ')}).</> : null}
                  {' '}Renaming this employee is safe.
                </div>
              )}
              {fpBlocked && (
                <div style={{ background: 'var(--red-bg)', color: 'var(--red-text)', borderRadius: 8, padding: '8px 12px', fontSize: 12, marginBottom: 6, lineHeight: 1.45 }}>
                  {footprint === null ? (
                    <div>Counting what is keyed on "{emp.name}"…</div>
                  ) : (
                    <>
                      <div style={{ fontWeight: 600 }}>⚠ #{emp.id} "{emp.name}" owns: {footprintParts(footprint, true).join(' · ') || 'unknown'}.</div>
                      <div style={{ marginTop: 4 }}>
                        Renaming this person does NOT rewrite those. Past field reports may drop off his own Field Reports list,
                        an office login loses its phone for escalations and announcements, and past schedule cells keep the old name.
                        {saferSibling
                          ? <> Set the display name on <strong>#{saferSibling.id} {saferSibling.name}</strong> instead — that record owns {footprintIdTotal(siblingFps[saferSibling.id]) === 0 ? 'nothing' : 'less'}.</>
                          : siblings.length ? <> Check the other {siblings.map(x => `#${x.id}`).join(', ')} record first.</> : null}
                      </div>
                      {displayChanged && (
                        <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span>Type <strong>{CONFIRM_WORD}</strong> to rename anyway:</span>
                          <input className="form-input" value={confirmText} onChange={e => setConfirmText(e.target.value)} style={{ width: 110, padding: '4px 8px', fontSize: 12 }} />
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
              <input className="form-input" value={displayName} onChange={e => setDisplayName(e.target.value)}
                placeholder={emp.foundationName || emp.name} />
              <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>
                Leave blank to use the Foundation name. Set this when two people share a name so the crew picker can tell them apart.
                {emp.displayName && <> Foundation name: <strong>{emp.foundationName}</strong>.</>}
              </div>
            </div>
          </FormRow>
        )}
        <FormRow label="Trade">
          <span style={{ fontSize: 13, color: 'var(--text2)', padding: '7px 0', flex: 1 }}>
            {trade || '—'} <span style={{ fontSize: 11, color: 'var(--text3)' }}>(synced from Foundation)</span>
          </span>
        </FormRow>
        <FormRow label="Local">
          <span style={{ fontSize: 13, color: 'var(--text2)', padding: '7px 0', flex: 1 }}>
            {local || '—'} <span style={{ fontSize: 11, color: 'var(--text3)' }}>(synced from Foundation)</span>
          </span>
        </FormRow>
        <FormRow label="Location">
          <span style={{ fontSize: 13, color: 'var(--text2)', padding: '7px 0', flex: 1 }}>
            {[emp?.city, emp?.state].filter(Boolean).join(', ') || '—'} <span style={{ fontSize: 11, color: 'var(--text3)' }}>(synced from Foundation)</span>
          </span>
        </FormRow>
        <FormRow label="Foundation Email">
          <span style={{ fontSize: 13, color: 'var(--text2)', padding: '7px 0', flex: 1 }}>
            {emp?.foundation_email || '—'} <span style={{ fontSize: 11, color: 'var(--text3)' }}>(synced from Foundation)</span>
          </span>
        </FormRow>
        <FormRow label="Notification Email">
          <input className="form-input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="foreman@cornerstonecontracting.net" />
          <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>Used for field report notifications. Never overwritten by sync.</div>
        </FormRow>
        <FormRow label="Role">
          <Checkbox checked={isForeman} onChange={setIsForeman} label="This person is a foreman" />
        </FormRow>
        <div className="modal-actions">
          <button className="btn btn-primary" onClick={handleSave}>{emp ? 'Save changes' : 'Add employee'}</button>
          <button className="btn" onClick={onCancel}>Cancel</button>
        </div>
      </div>
    </div>
  )
}

// ── Job form ───────────────────────────────────────────────────────────────
function JobForm({ job, jobs, onSave, onCancel }) {
  const [num, setNum] = useState(job?.num || '')
  const [name, setName] = useState(job?.name || '')
  const [address, setAddress] = useState(job?.address || '')
  const [pm, setPm] = useState(job?.pm || '')
  const [gc, setGc] = useState(job?.gc || '')
  const [active, setActive] = useState(job?.active !== false)
  const [error, setError] = useState('')

  const handleSave = () => {
    if (!num.trim()) { setError('Job number is required'); return }
    if (!name.trim()) { setError('Job name is required'); return }
    if (jobs.some(j => j.num === num.trim() && j.id !== job?.id)) {
      setError(`Job number ${num} already exists`); return
    }
    onSave({ num: num.trim(), name: name.trim(), address: address.trim(), pm: pm.trim(), gc: gc.trim(), active })
  }

  return (
    <div className="modal-backdrop" onClick={onCancel}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-title">{job ? 'Edit job' : 'Add job'}</div>
        <div className="modal-sub">{job ? `Editing ${job.num} ${job.name}` : 'Add a new job to the board'}</div>
        {error && <div style={{ background: 'var(--red-bg)', color: 'var(--red-text)', borderRadius: 8, padding: '8px 12px', fontSize: 12, marginBottom: 10 }}>{error}</div>}
        <FormRow label="Job #">
          <span style={{ fontSize: 13, color: 'var(--text2)', padding: '7px 0', flex: 1 }}>{num || '—'} <span style={{ fontSize: 11, color: 'var(--text3)' }}>(synced from Foundation)</span></span>
        </FormRow>
        <FormRow label="Job name">
          <span style={{ fontSize: 13, color: 'var(--text2)', padding: '7px 0', flex: 1 }}>{name || '—'} <span style={{ fontSize: 11, color: 'var(--text3)' }}>(synced from Foundation)</span></span>
        </FormRow>
        <FormRow label="Address">
          <span style={{ fontSize: 13, color: 'var(--text2)', padding: '7px 0', flex: 1 }}>{address || '—'} <span style={{ fontSize: 11, color: 'var(--text3)' }}>(synced from Foundation)</span></span>
        </FormRow>
        <FormRow label="PM">
          <span style={{ fontSize: 13, color: 'var(--text2)', padding: '7px 0', flex: 1 }}>{job?.pmName || pm || '—'} <span style={{ fontSize: 11, color: 'var(--text3)' }}>(synced from Foundation)</span></span>
        </FormRow>
        <FormRow label="GC">
          <span style={{ fontSize: 13, color: 'var(--text2)', padding: '7px 0', flex: 1 }}>{gc || '—'} <span style={{ fontSize: 11, color: 'var(--text3)' }}>(synced from Foundation)</span></span>
        </FormRow>
        <FormRow label="Status">
          <Checkbox checked={active} onChange={setActive} label="Active (show on board)" />
        </FormRow>
        <div className="modal-actions">
          <button className="btn btn-primary" onClick={handleSave}>{job ? 'Save changes' : 'Add job'}</button>
          <button className="btn" onClick={onCancel}>Cancel</button>
        </div>
      </div>
    </div>
  )
}

// ── Confirm delete dialog ──────────────────────────────────────────────────
function ConfirmDialog({ message, onConfirm, onCancel }) {
  return (
    <div className="modal-backdrop" onClick={onCancel}>
      <div className="modal" style={{ maxWidth: 340 }} onClick={e => e.stopPropagation()}>
        <div className="modal-title" style={{ marginBottom: 10 }}>Are you sure?</div>
        <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 18 }}>{message}</div>
        <div className="modal-actions">
          <button className="btn btn-danger" onClick={onConfirm}>Delete</button>
          <button className="btn" onClick={onCancel}>Cancel</button>
        </div>
      </div>
    </div>
  )
}

// ── Main Admin Panel ───────────────────────────────────────────────────────
export default function AdminPanel({ roster, jobs, onUpdateRoster, onUpdateJobs }) {
  const [view, setView] = useState('employees') // 'employees' | 'jobs'
  const [empForm, setEmpForm] = useState(null)   // null | 'new' | employee object
  const [jobForm, setJobForm] = useState(null)   // null | 'new' | job object
  const [confirm, setConfirm] = useState(null)   // null | { message, onConfirm }
  const [search, setSearch] = useState('')
  const [syncing, setSyncing] = useState(false)
  const [syncResult, setSyncResult] = useState(null)
  const [syncLog, setSyncLog] = useState(null)
  const [nameMsg, setNameMsg] = useState(null)   // { ok, text } after a display-name change

  React.useEffect(() => {
    loadSyncLog().then(log => setSyncLog(log))
  }, [])

  const handleSync = async () => {
    setSyncing(true)
    setSyncResult(null)
    try {
      const anonKey = process.env.REACT_APP_SUPABASE_ANON_KEY
      const result = await triggerFoundationSync(anonKey)
      setSyncResult(result)
      if (result.success) {
        setSyncLog({ last_sync: new Date().toISOString(), jobs_synced: result.jobs, employees_synced: result.employees })
        // Reload the page after sync so fresh data loads from Supabase
        setTimeout(() => window.location.reload(), 1500)
      }
    } catch (e) {
      setSyncResult({ success: false, error: e.message })
    }
    setSyncing(false)
  }

  // ── Employee actions ───────────────────────────────────────────────────
  const saveEmployee = async (data) => {
    if (empForm === 'new') {
      const newId = Math.max(...roster.map(e => e.id), 0) + 1
      onUpdateRoster([...roster, { id: newId, ...data, timeOff: [] }])
      setEmpForm(null)
      return
    }
    // Only update email and isForeman — everything else comes from Foundation sync
    let next = { ...empForm, email: data.email, isForeman: data.isForeman }
    // Save directly to Supabase — saveAllEmployees never touches the email column
    saveEmployeeNotificationEmail(empForm.id, data.email, data.isForeman)

    // Display name (same-name override). Write the override + recomputed name first,
    // THEN carry the new name onto this person's upcoming schedule cells. History keeps
    // the name it was dispatched under.
    const newDisplay = (data.displayName || '').trim()
    if (newDisplay !== (empForm.displayName || '').trim()) {
      const r = await setEmployeeDisplayName(empForm.id, newDisplay, empForm.foundationName || empForm.name)
      if (r.error) {
        setNameMsg({ ok: false, text: `Display name NOT saved: ${r.error}` })
      } else {
        const { cells, warnings } = await renameInFutureSchedule(empForm.id, empForm.name, r.name)
        next = { ...next, name: r.name, displayName: newDisplay }
        const bits = [`Now shown as "${r.name}". Updated ${cells} upcoming schedule cell${cells === 1 ? '' : 's'}.`, ...warnings]
        if (cells > 0) bits.push('Reloading so the board picks up the new name…')
        setNameMsg({ ok: warnings.length === 0, text: bits.join(' ') })
        // The board holds its cells in memory; saving a stale cell would write the OLD
        // name back (and lose the id). Reload rather than leave that window open.
        if (cells > 0) setTimeout(() => window.location.reload(), 2500)
      }
    }
    onUpdateRoster(roster.map(e => e.id === empForm.id ? next : e))
    setEmpForm(null)
  }

  // Active employees sharing one effective name — the crew picker can only add one of
  // them until a display name tells them apart.
  const dupGroups = (() => {
    const m = {}
    roster.filter(e => e.active !== false).forEach(e => { const k = nameKeyOf(e.name); if (!k) return; (m[k] = m[k] || []).push(e) })
    return Object.values(m).filter(g => g.length > 1).sort((a, b) => a[0].name.localeCompare(b[0].name))
  })()

  const deleteEmployee = (emp) => {
    setConfirm({
      message: `Remove ${emp.name} from the roster? This cannot be undone.`,
      onConfirm: () => { onUpdateRoster(roster.filter(e => e.id !== emp.id), emp.id); setConfirm(null) }
    })
  }

  // ── Job actions ────────────────────────────────────────────────────────
  const saveJob = (data) => {
    if (jobForm === 'new') {
      const newId = Math.max(...jobs.map(j => j.id), 0) + 1
      const colorIdx = jobs.length % PROJ_COLORS.length
      onUpdateJobs([...jobs, { id: newId, color: PROJ_COLORS[colorIdx], ...data }])
    } else {
      onUpdateJobs(jobs.map(j => j.id === jobForm.id ? { ...j, ...data } : j))
      // The form's Active checkbox: persist as a targeted write + sync-durable
      // override (saveAllJobs no longer writes active).
      if (data.active !== (jobForm.active !== false)) {
        saveActiveOverride('job', jobForm.id, data.active)
        setJobActive(jobForm.id, data.active)
      }
    }
    setJobForm(null)
  }

  const deleteJob = (job) => {
    setConfirm({
      message: `Remove ${job.num} – ${job.name} from the job list? This cannot be undone.`,
      onConfirm: () => { onUpdateJobs(jobs.filter(j => j.id !== job.id), job.id); setConfirm(null) }
    })
  }

  const toggleJobActive = (job) => {
    const newActive = !job.active
    onUpdateJobs(jobs.map(j => j.id === job.id ? { ...j, active: newActive } : j))
    // Sync-durable override + targeted write (saveAllJobs no longer touches active).
    saveActiveOverride('job', job.id, newActive)
    setJobActive(job.id, newActive)
  }

  const toggleEmpActive = (emp) => {
    const newActive = !emp.active
    onUpdateRoster(roster.map(e => e.id === emp.id ? { ...e, active: newActive } : e))
    // Sync-durable override + targeted write (saveAllEmployees no longer touches active).
    saveActiveOverride('employee', emp.id, newActive)
    setEmployeeActive(emp.id, newActive)
  }

  // Office staff (PMs, owners, admins, drivers) stay ACTIVE — they belong on the roster,
  // the time-off screens, field reports and the texting audience — but are taken off the
  // SCHEDULING board so they stop cluttering the crew picker, the foreman dropdowns and
  // the Unassigned lists. Manual only, never derived from app_users.role. No
  // saveActiveOverride twin needed: neither the Foundation sync nor saveAllEmployees
  // writes this column, so the setting is sync-durable on its own.
  const toggleEmpSchedulable = (emp) => {
    const next = emp.schedulable === false
    onUpdateRoster(roster.map(e => e.id === emp.id ? { ...e, schedulable: next } : e))
    setEmployeeSchedulable(emp.id, next)
  }

  // ── Filter ─────────────────────────────────────────────────────────────
  const filteredRoster = roster.filter(e =>
    e.name.toLowerCase().includes(search.toLowerCase()) ||
    e.local.toLowerCase().includes(search.toLowerCase())
  ).sort((a, b) => {
    if (a.isForeman && !b.isForeman) return -1
    if (!a.isForeman && b.isForeman) return 1
    return a.name.localeCompare(b.name)
  })

  const filteredJobs = jobs.filter(j =>
    j.name.toLowerCase().includes(search.toLowerCase()) ||
    j.num.toLowerCase().includes(search.toLowerCase()) ||
    (j.pm || '').toLowerCase().includes(search.toLowerCase()) ||
    (j.gc || '').toLowerCase().includes(search.toLowerCase())
  ).sort((a, b) => {
    // Job numbers are numeric for real jobs but TEXT for overhead jobs ('SHOP').
    // parseInt('SHOP') is NaN, and a NaN comparator result scrambles neighbouring
    // entries — so sort numeric nums numerically and park non-numeric ones at the
    // end, alphabetically.
    const na = parseInt(a.num, 10), nb = parseInt(b.num, 10)
    const aNum = !isNaN(na), bNum = !isNaN(nb)
    if (aNum && bNum) return na - nb
    if (aNum) return -1
    if (bNum) return 1
    return String(a.num).localeCompare(String(b.num))
  })

  const activeJobs = filteredJobs.filter(j => j.active !== false)
  const inactiveJobs = filteredJobs.filter(j => j.active === false)
  const foremen = filteredRoster.filter(e => e.isForeman)
  const workers = filteredRoster.filter(e => !e.isForeman)

  return (
    <div style={{ maxWidth: 700 }}>
      {/* Foundation sync panel */}
      <div style={{ background: 'var(--blue-bg)', border: '0.5px solid #B8D4F0', borderRadius: 'var(--radius-lg)', padding: '10px 14px', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--blue-text)' }}>Foundation / Payroll4Construction sync</div>
            <div style={{ fontSize: 11, color: 'var(--blue-text)', opacity: .8, marginTop: 2 }}>
              {syncLog ? `Last synced: ${new Date(syncLog.last_sync).toLocaleString()} · ${syncLog.jobs_synced} jobs · ${syncLog.employees_synced} employees` : 'Never synced — click to pull from Foundation'}
            </div>
          </div>
          <button className="btn btn-primary btn-sm" onClick={handleSync} disabled={syncing}>
            {syncing ? 'Syncing...' : '↻ Sync now'}
          </button>
        </div>
        {syncResult && (
          <div style={{ marginTop: 8, fontSize: 12, padding: '6px 10px', borderRadius: 6,
            background: syncResult.success ? 'var(--green-bg)' : 'var(--red-bg)',
            color: syncResult.success ? 'var(--green-text)' : 'var(--red-text)' }}>
            {syncResult.success
              ? `✓ Synced ${syncResult.jobs} jobs and ${syncResult.employees} employees successfully`
              : `✗ Sync failed: ${syncResult.error}`}
          </div>
        )}
      </div>

      {/* Sub-nav */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
        <button
          className={`btn ${view === 'employees' ? 'btn-primary' : ''}`}
          onClick={() => { setView('employees'); setSearch('') }}
        >
          Employees ({roster.length})
        </button>
        <button
          className={`btn ${view === 'jobs' ? 'btn-primary' : ''}`}
          onClick={() => { setView('jobs'); setSearch('') }}
        >
          Jobs ({jobs.length})
        </button>
      </div>

      {/* Search + add */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <input
          className="form-input"
          style={{ flex: 1 }}
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder={view === 'employees' ? 'Search by name or local...' : 'Search by job #, name, PM, or GC...'}
        />
        {view === 'employees' && (
          <button className="btn" onClick={() => exportContactsVcf(roster)} title="Download a .vcf file with active employees' contact info">
            Export Contacts (.vcf)
          </button>
        )}
        {view === 'jobs' && (
          <button className="btn btn-primary" onClick={() => setJobForm('new')}>+ Add job</button>
        )}
      </div>

      {/* ── EMPLOYEES VIEW ─────────────────────────────────────────────── */}
      {view === 'employees' && nameMsg && (
        <div style={{ marginBottom: 12, fontSize: 12, padding: '8px 12px', borderRadius: 8,
          background: nameMsg.ok ? 'var(--green-bg)' : 'var(--amber-bg)', color: nameMsg.ok ? 'var(--green-text)' : 'var(--amber-text)',
          display: 'flex', justifyContent: 'space-between', gap: 10 }}>
          <span>{nameMsg.text}</span>
          <span onClick={() => setNameMsg(null)} style={{ cursor: 'pointer', fontWeight: 700 }}>×</span>
        </div>
      )}
      {view === 'employees' && dupGroups.map(g => (
        <div key={g[0].name} style={{ marginBottom: 8, fontSize: 12, padding: '8px 12px', borderRadius: 8,
          background: 'var(--amber-bg)', color: 'var(--amber-text)', border: '0.5px solid var(--amber-text)' }}>
          ⚠ {g.length} active employees named <strong>{g[0].name}</strong> ({g.map(e => `#${e.id}`).join(', ')}) — set a display name on the one with no schedule history so the crew picker can tell them apart.
        </div>
      ))}
      {view === 'employees' && (
        <div className="card">
          {/* Foremen section */}
          {foremen.length > 0 && (
            <>
              <div className="section-lbl">Foremen ({foremen.length})</div>
              {foremen.map(emp => (
                <EmployeeRow key={emp.id} emp={emp} onEdit={() => setEmpForm(emp)} onToggleActive={() => toggleEmpActive(emp)} onToggleSchedulable={() => toggleEmpSchedulable(emp)} />
              ))}
            </>
          )}
          {/* Workers section */}
          {workers.length > 0 && (
            <>
              <div className="section-lbl">Workers ({workers.length})</div>
              {workers.map(emp => (
                <EmployeeRow key={emp.id} emp={emp} onEdit={() => setEmpForm(emp)} onToggleActive={() => toggleEmpActive(emp)} onToggleSchedulable={() => toggleEmpSchedulable(emp)} />
              ))}
            </>
          )}
          {filteredRoster.length === 0 && (
            <div style={{ padding: 32, textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>
              {search ? 'No employees match your search.' : 'No employees yet. Click + Add employee to get started.'}
            </div>
          )}
        </div>
      )}

      {/* ── JOBS VIEW ──────────────────────────────────────────────────── */}
      {view === 'jobs' && (
        <div className="card">
          {activeJobs.length > 0 && (
            <>
              <div className="section-lbl">Active jobs ({activeJobs.length})</div>
              {activeJobs.map(job => (
                <JobRow key={job.id} job={job} onEdit={() => setJobForm(job)} onToggleActive={() => toggleJobActive(job)} />
              ))}
            </>
          )}
          {inactiveJobs.length > 0 && (
            <>
              <div className="section-lbl" style={{ marginTop: 8 }}>Inactive / closed ({inactiveJobs.length})</div>
              {inactiveJobs.map(job => (
                <JobRow key={job.id} job={job} onEdit={() => setJobForm(job)} onToggleActive={() => toggleJobActive(job)} />
              ))}
            </>
          )}
          {filteredJobs.length === 0 && (
            <div style={{ padding: 32, textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>
              {search ? 'No jobs match your search.' : 'No jobs yet. Click + Add job to get started.'}
            </div>
          )}
        </div>
      )}

      {/* ── Modals ─────────────────────────────────────────────────────── */}
      {empForm && (
        <EmployeeForm
          emp={empForm === 'new' ? null : empForm}
          roster={roster}
          onSave={saveEmployee}
          onCancel={() => setEmpForm(null)}
        />
      )}
      {jobForm && (
        <JobForm
          job={jobForm === 'new' ? null : jobForm}
          jobs={jobs}
          onSave={saveJob}
          onCancel={() => setJobForm(null)}
        />
      )}
      {confirm && (
        <ConfirmDialog
          message={confirm.message}
          onConfirm={confirm.onConfirm}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  )
}

// ── Row sub-components ─────────────────────────────────────────────────────
function EmployeeRow({ emp, onEdit, onDelete, onToggleActive, onToggleSchedulable }) {
  const AVATAR_BG = ['#E6F1FB','#E1F5EE','#FAECE7','#EEEDFE','#FAEEDA','#FBEAF0']
  const AVATAR_FG = ['#0C447C','#085041','#712B13','#3C3489','#633806','#72243E']
  const idx = emp.id % 6
  const init = emp.name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()
  const isActive = emp.active !== false
  const onBoard = emp.schedulable !== false

  return (
    <div className="emp-row" style={{ opacity: isActive ? 1 : 0.55 }}>
      <div className="avatar" style={{ background: AVATAR_BG[idx], color: AVATAR_FG[idx] }}>{init}</div>
      <div className="emp-info">
        <div className="emp-name">
          {emp.name}
          {!isActive && <span style={{ marginLeft: 6, fontSize: 10, background: 'var(--surface2)', color: 'var(--text3)', borderRadius: 4, padding: '1px 6px', border: '0.5px solid var(--border)' }}>Inactive</span>}
        </div>
        <div className="emp-meta">
          {emp.local}
          {emp.displayName && <span style={{ marginLeft: 6, fontSize: 10, color: 'var(--text3)' }} title="Name as it comes from Foundation; the display name is an office override">(Foundation: {emp.foundationName})</span>}
          {emp.isForeman && <span style={{ marginLeft: 6, fontSize: 10, background: 'var(--blue-bg)', color: 'var(--blue-text)', borderRadius: 4, padding: '1px 6px' }}>Foreman</span>}
          {!onBoard && <span style={{ marginLeft: 6, fontSize: 10, background: 'var(--surface2)', color: 'var(--text3)', borderRadius: 4, padding: '1px 6px', border: '0.5px solid var(--border)' }} title="Active on the roster, but never offered in the crew picker, the foreman dropdowns or the Unassigned lists">Office — off board</span>}
          <span style={{ marginLeft: 6, fontSize: 10, color: 'var(--text3)' }} title="Payroll employee number (Foundation / P4C)">#{emp.id}</span>
        </div>
        <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 1, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {emp.cellPhone && <span>📱 {fmtPhone(emp.cellPhone)}</span>}
          {emp.cellPhone && emp.phone && <span>·</span>}
          {emp.phone && !emp.cellPhone && <span>☎ {fmtPhone(emp.phone)}</span>}
          {(emp.city || emp.state) && <span>📍 {[emp.city, emp.state].filter(Boolean).join(', ')}</span>}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 6, flexShrink: 0, alignItems: 'center' }}>
        <button className="btn btn-sm" onClick={onToggleActive} style={{ fontSize: 11 }}>
          {isActive ? 'Mark inactive' : 'Mark active'}
        </button>
        {onToggleSchedulable && (
          <button className="btn btn-sm" onClick={onToggleSchedulable} style={{ fontSize: 11 }}
            title="Office staff stay on the roster and in the texting audience, but are never offered when building a crew">
            {onBoard ? 'Hide from board' : 'Show on board'}
          </button>
        )}
        <button className="btn btn-sm" onClick={onEdit}>Edit</button>

      </div>
    </div>
  )
}

function JobRow({ job, onEdit, onDelete, onToggleActive }) {
  const isActive = job.active !== false
  return (
    <div className="emp-row">
      <div style={{ width: 4, height: 36, borderRadius: 2, background: job.color || '#ccc', flexShrink: 0 }} />
      <div className="emp-info">
        <div className="emp-name">
          {job.num} — {job.name}
          {!isActive && <span style={{ marginLeft: 6, fontSize: 10, background: 'var(--surface2)', color: 'var(--text3)', borderRadius: 4, padding: '1px 6px', border: '0.5px solid var(--border)' }}>Inactive</span>}
        </div>
        <div className="emp-meta">
          {job.address}{(job.pmName || job.pm) ? ` · PM: ${job.pmName || job.pm}` : ''}{job.gc ? ` · GC: ${job.gc}` : ''}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 6, flexShrink: 0, alignItems: 'center' }}>
        <button className="btn btn-sm" onClick={onToggleActive} style={{ fontSize: 11 }}>
          {isActive ? 'Mark inactive' : 'Mark active'}
        </button>
        <button className="btn btn-sm" onClick={onEdit}>Edit</button>

      </div>
    </div>
  )
}
